import { useState, useEffect } from 'react';
import { collection, query, onSnapshot, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { getUserRestaurant } from '../utils/restaurantUtils';
import AdminNavigation from '../components/AdminNavigation';
import { 
  Clock,
  CheckCircle2,
  ChefHat,
  Loader
} from 'lucide-react';
import WaiterCallsPanel from '../components/WaiterCallsPanel';

function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      const resId = await getUserRestaurant(auth.currentUser.uid);
      setRestaurantId(resId || 'demo-restaurant');
    };
    fetchRestaurant();
  }, [navigate]);

  useEffect(() => {
    if (!restaurantId) return;

    const ordersQuery = query(
      collection(db, 'restaurants', restaurantId, 'orders'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [restaurantId]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'restaurants', restaurantId, 'orders', orderId);
      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order status');
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter);

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      preparing: 'badge-preparing',
      ready: 'badge-ready',
      completed: 'badge-completed'
    };
    return badges[status] || 'badge-pending';
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      pending: 'preparing',
      preparing: 'ready',
      ready: 'completed',
      completed: 'completed'
    };
    return statusFlow[currentStatus];
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Start Preparing',
      preparing: 'Mark Ready',
      ready: 'Mark Completed',
      completed: 'Completed'
    };
    return labels[status];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        {/* Waiter Calls Panel */}
        {restaurantId && <WaiterCallsPanel restaurantId={restaurantId} />}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-6">
              {['all', 'pending', 'preparing', 'ready', 'completed'].map(status => (
                <button
                  key={status}
              onClick={() => setFilter(status)}
              className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                filter === status
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              <span className="ml-1 text-[10px] sm:text-xs">
                ({orders.filter(o => o.status === status).length})
              </span>
            </button>
          ))}
        </div>

        {/* Orders Grid */}
        {filteredOrders.length === 0 ? (
          <div className="card text-center py-12">
            <UtensilsCrossed className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No orders found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filteredOrders.map(order => (
              <div key={order.id} className="card hover:shadow-xl transition-all duration-200 shadow-sm p-4 flex flex-col">
                {/* Compact Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900">
                        Table {order.tableId}
                      </h3>
                      {order.status !== 'completed' && (
                        <span className={`badge ${getStatusBadge(order.status)} text-xs`}>
                          {order.status}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {order.createdAt?.toDate?.().toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                      <span className="text-gray-300">•</span>
                      <span className="font-mono text-gray-400">#{order.id.slice(0, 8)}</span>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-primary-600 ml-2 flex-shrink-0">
                    €{order.total?.toFixed(2)}
                  </p>
                </div>

                {/* Compact Items List */}
                <div className="space-y-1.5 mb-3 flex-1">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm py-1.5 px-2 bg-gray-50 rounded">
                      <span className="text-gray-700 flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 bg-primary-100 text-primary-700 rounded text-xs font-semibold px-1.5 flex-shrink-0">
                          {item.quantity}
                        </span>
                        <span className="truncate text-xs">{item.name}</span>
                      </span>
                      <span className="font-semibold text-gray-900 ml-2 flex-shrink-0 text-xs">
                        €{item.subtotal?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Special Instructions - Compact */}
                {order.specialInstructions && (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                    <span className="font-semibold text-amber-900">📝</span>{' '}
                    <span className="text-amber-800">{order.specialInstructions}</span>
                  </div>
                )}

                {/* Compact Actions */}
                <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                  {order.status !== 'completed' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, getNextStatus(order.status))}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg text-sm font-medium"
                    >
                      {order.status === 'pending' && <ChefHat className="w-4 h-4" />}
                      {order.status === 'preparing' && <CheckCircle2 className="w-4 h-4" />}
                      {order.status === 'ready' && <CheckCircle2 className="w-4 h-4" />}
                      <span>{getStatusLabel(order.status)}</span>
                    </button>
                  )}
                  {order.status === 'completed' && (
                    <div className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 rounded-lg font-semibold border border-green-200 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Completed</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export default Orders;
